import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";

import { Box, Grid, Typography } from "@mui/material";

import ContactSupportButton from "./buttons/ContactSupportButton";

import { useAccountData, useAuth } from "../../helpers/firebase";
import { sendSupportEmail } from "../../helpers/utils/apiCalls";
import { stripSensitiveUrlParams } from "../../helpers/otherHelpers";

const ErrorScreen = () => {
  const [{ name: companyName }, companyDataLoading] = useAccountData();
  const [user, userLoading] = useAuth();

  const [messageSent, setMessageSent] = useState(false);

  useEffect(() => {
    if (
      !userLoading &&
      !messageSent &&
      !companyDataLoading &&
      process.env.REACT_APP_ENVIRONMENT === "production"
    ) {
      const { displayName, email } = user || {};
      sendSupportEmail({
        name: displayName,
        companyName,
        email,
        subject: "Sentry Error Detected",
        message: `An error occurred at ${stripSensitiveUrlParams(window.location.href)}`,
      });
      setMessageSent(true);
    }
  }, [user, userLoading, companyName, messageSent, companyDataLoading]);

  return (
    <Box p={8} display="flex" alignItems="center" justifyContent="center">
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Typography variant="h5" align="center">
            We&apos;re sorry, but unfortunately an error occurred.
          </Typography>
          <Typography variant="subtitle1" align="center" color="textSecondary">
            We&apos;ve already been alerted, but you can send us an email if you want
            to provide more details
          </Typography>
        </Grid>
        <Grid item container justifyContent="center" spacing={2}>
          <Grid item>
            <ContactSupportButton
              email={user?.email}
              userName={user?.displayName}
              companyName={companyName}
              subject="Error in the Aclymate app"
              message={`I ran into an error at ${window.location.href}\n\nWhat I was doing: `}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

const ErrorBoundary = ({ children }) => {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorScreen />}>
      {children}
    </Sentry.ErrorBoundary>
  );
};

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

export default ErrorBoundary;
